import { css } from '@emotion/react';
import {
  halfSpacing, lectureBottomPanelZIndex,
  standardSpacing, resourceCenterPendoBadgeZIndex,
  largeSpacing,
  doubleSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import React, { useContext } from 'react';
import ClickableContainer from 'components/clickable-container';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { setLeftPanelVisibility, setTimelineIsExpanded } from 'redux/actions/lecture-pages';
import { useAppDispatch } from 'redux/store';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { black, gray4, gray5, gray6, highTide, info, primary, warning, white } from 'styles/global_defaults/colors';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { screenSmMax, screenXsMax } from 'styles/global_defaults/media-queries';
import { useLecturePageFromParams, useLecturePageLink, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import BookmarkIcon from 'bookmarks/components/bookmark-icon';
import { createBookmark, highlightBookmark } from 'redux/actions/bookmarks';
import { BookmarkType } from 'redux/schemas/models/bookmark';
import { useCourseOutline } from 'redux/selectors/course-outline/use-course-outline';
import { getNextLecture, getPreviousLecture } from 'redux/selectors/lecture-page';
import { AngularServicesContext } from 'react-app';
import { useMediaQuery } from 'react-responsive';
import { setShowCourseCompletionStatusModal } from 'redux/actions/courses';
import { CourseCompletionStatusModal } from 'course_home/components/course-completion-status-modal';
import { setCourseModalViewed } from 'course_home/services/course-modal-viewed';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { PreventNavigationReason } from 'redux/schemas/models/lecture-page';
import { config } from '@config/pendo.config.json';
import AskNovoAI from 'institutions/components/ask-novoai';

/**
 * Contains Back/Forward lecture buttons and a progress bar showing current progress
 * through the course and collection.
 */
export const LecturePageBottomPanel = () => {
  const currentUserId = useSelector(state => state.app.currentUserId);
  const catalogId = useSelector(state => state.app.currentCatalogId);
  const { nextCourseButtonEnabled } = useSelector(getCurrentCourse);
  const { preventPageNavigation, isLecturePageBottomReached } = useSelector(state => state.app.lecturePage);

  React.useEffect(() => {
    dispatch(setShowCourseCompletionStatusModal(false));
  }, []);

  const isNotDesktop = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const styles = css`
    position: fixed;
    bottom: 0;
    /* Set the width based on the LHS nav view. The LHS only shows on desktop      .
     * screens. So reducing the LHS width from total width in desktop screens and
     * using full width on non desktop screens */
    width: calc(100vw - 60px);
    ${isNotDesktop && css`
      width: 100%;
    `}
    height: ${tripleSpacing}px;
    z-index: ${lectureBottomPanelZIndex};
    margin-top: ${largeSpacing}px;
    margin-bottom: ${largeSpacing}px;

    display: flex;
    align-items: center;

    .outline-icon {
      border: 1px solid ${gray6};
      background: ${white};
      box-shadow: 0px ${halfSpacing}px ${standardSpacing}px 0px rgba(29, 33, 38, 0.2);
    }

    .nav-pill {
      display: flex;
      align-items: center;
      background: ${white};
      border-radius: 100px;
      border: 1px solid ${gray6};
      padding: ${halfSpacing}px;
      box-shadow: 0px ${halfSpacing}px ${standardSpacing}px rgba(29, 33, 38, 0.2);

      ${isHandheld && css`
        justify-content: space-between;
        padding: ${halfSpacing}px ${standardSpacing}px;
      `}

      ${!isHandheld && css`
        width: fit-content;
        margin: auto;
      `}
    }

    .divider {
      width: 1px;
      height: ${doubleSpacing}px;
      background: ${gray5};
      margin: 0 ${halfSpacing}px;
    }

    .previous,
    .next {
      position: static;
    }

    .toggle-button-container {
      user-select: false;

      :hover .outline-icon {
        background: ${info};
      }
    }

    .bookmark-styles {
      border: 1px solid ${gray6};
      box-shadow: 0px ${halfSpacing}px ${standardSpacing}px 0px rgba(29, 33, 38, 0.2);
      border-radius: 50%;
      background: ${white};

      :hover {
        background: ${info};
      }
    }

    .icon-btn {
      width: ${doubleSpacing}px;
      height: ${doubleSpacing}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      cursor: pointer;

      &:hover {
        background: ${info};
      }
    }

    .ask-novoai {
      width: auto;
    }
 `;

  const dispatch = useAppDispatch();
  const { isLeftPanelVisible } = useSelector(state => state.app.lecturePage);

  const { FlyoutModalManager } = useContext(AngularServicesContext);

  const toggleLeftPanelVisible = () => {
    const newVisibility = !isLeftPanelVisible;
    dispatch(setLeftPanelVisibility(newVisibility));

    if (!isNotDesktop) {
      dispatch(setTimelineIsExpanded(newVisibility));
    }
    // Added because for some reason is not being called automatically on colored dev servers, even if
    // it's running in local dev mode.
    window.dispatchEvent(new Event('resize'));
  };

  const params = useLecturePageParams();
  const lecturePage = useLecturePageFromParams();
  const lectureLink = useLecturePageLink();

  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const isLectureLoaded = useSelector(state => lecturePage && state.app.lecturePage.isCurrentLectureLoaded);

  const courseOutline = useCourseOutline(params.catalogId);
  const previousLecture = getPreviousLecture(courseOutline, params.lecturePageId);
  const nextLecture = getNextLecture(courseOutline, params.lecturePageId);

  const navigateBack = isLectureLoaded && previousLecture && (() => {
    lectureLink({
      ...params,
      lecturePageId: previousLecture.id,
    });
  });

  // Navigate to the next lesson or show the course completion status modal
  const navigateForward = ((isLectureLoaded && nextLecture) || nextCourseButtonEnabled) && (() => {
    const showCourseCompletionStatusModal = () => {
      setCourseModalViewed(currentUserId, catalogId);
      dispatch(setShowCourseCompletionStatusModal(true));
    };

    if (isLectureLoaded && nextLecture) {
      lectureLink({
        ...params,
        lecturePageId: nextLecture.id,
      });
    } else if (!nextLecture && nextCourseButtonEnabled) {
      // Check for unsaved changes in lecture page to display the overlay
      if (preventPageNavigation === PreventNavigationReason.SAVING_IN_PROGRESS) {
        dispatch(openConfirmationDialog({
          icon: 'warning',
          title: t.FORM.SAVING_CHANGES.NAVIGATE_AWAY(),
          cancelText: t.FORM.CANCEL(),
          confirmText: t.FORM.SAVING_CHANGES.OK(),
          onConfirm: showCourseCompletionStatusModal,
        }));
      } else {
        showCourseCompletionStatusModal();
      }
    }
  });

  const onCreateBookmark = () => {
    dispatch(createBookmark({
      type: BookmarkType.LECTURE_PAGE,
      catalogId: params.catalogId,
      componentId: params.lecturePageId,
    })).then(() => {
      FlyoutModalManager.openFlyout({
        controller: 'BookmarksFlyoutCtrl as vm',
        template: 'bookmarks/templates/bookmarks-flyout-react-app.html',
        isNavigational: false,
      });
    });
  };

  const onHighlightBookmark = () => {
    dispatch(highlightBookmark({ id: lecturePage.bookmarkId }));
    FlyoutModalManager.openFlyout({
      controller: 'BookmarksFlyoutCtrl as vm',
      template: 'bookmarks/templates/bookmarks-flyout-react-app.html',
      isNavigational: false,
    });
  };

  const OutlineButton = () => {
    return (
      <NvTooltip
        text={
          isLeftPanelVisible
            ? t.LECTURE_PAGES.CLOSE_OUTLINE(courseAliases)
            : t.LECTURE_PAGES.OPEN_OUTLINE(courseAliases)
        }
      >
        <ClickableContainer
          onClick={() => toggleLeftPanelVisible()}
          role='button'
          tabIndex={0}
          aria-expanded={isLeftPanelVisible}
          aria-label={t.LECTURE_PAGES.OUTLINE()}
          data-qa={config.pendo.lecturePage.openCloseIconOutline}
        >
          <div
            className={isHandheld ? 'icon-btn' : 'outline-icon text-black rounded-circle p-4'}
            data-qa={config.pendo.lecturePage[isLeftPanelVisible ? 'closeOutline' : 'openOutline']}
          >
            <NvIcon size='small' icon={isLeftPanelVisible ? 'close' : 'course-menu'} />
          </div>
        </ClickableContainer>
      </NvTooltip>
    )
  }

  const PreviousButton = () => {
    return (
      <NvTooltip
        text={previousLecture && t.LECTURE_PAGES.PREVIOUS_LECTURE(previousLecture.name)}
        enabled={!!previousLecture}
      >
        {/* This inner div must be present in order for React to insert these NvTooltips. I'm unsure
          as to why but it seems like an issue with React.Fragment */}
        <div className='previous' data-qa={config.pendo.lecturePage.previousPage}>
          <NavigationButton direction='previous' navigate={navigateBack} />
        </div>
      </NvTooltip>
    )
  }

  const NextButton = () => {
    return (
      <NvTooltip
        text={nextLecture && t.LECTURE_PAGES.NEXT_LECTURE(nextLecture.name)}
        enabled={!!nextLecture}
      >
        <div className='next' data-qa={config.pendo.lecturePage.nextPage}>
          <NavigationButton
            direction='next'
            navigate={navigateForward}
            color={(isLecturePageBottomReached && isLectureLoaded) ? primary : white}
            isHighlighted={isLecturePageBottomReached && isLectureLoaded}
          />
        </div>
      </NvTooltip>
    )
  }

  const BookMarkButton = () => {
    return (
      <React.Fragment>
        {params.catalogId && (
          <BookmarkIcon
            size='small'
            bookmarked={!!lecturePage?.bookmarkId}
            onCreate={onCreateBookmark}
            onHighlight={onHighlightBookmark}
            createTooltip={t.LHS.BOOKMARKS.ADD_BOOKMARK.LESSON(courseAliases)}
            altText={t.LHS.BOOKMARKS.ADD_BOOKMARK.LESSON(courseAliases)}
            tooltipZIndex={resourceCenterPendoBadgeZIndex + 1}
            className={isHandheld ? 'icon-btn' : 'bookmark-styles p-4'}
          />
        )}
      </React.Fragment>
    )
  }

  // TODO: The prod app has an iframe covering this bottom panel. Why?
  return (
    <nav
      css={styles}
      aria-label={t.LECTURE_PAGES.LESSON_NAVIGATION(courseAliases)}
    >
      <div className='d-flex justify-content-between w-100 align-items-center h-100'>
        {isHandheld && (
          <div className='w-100 d-flex justify-content-center pr-5 pl-5'>
            <div className='w-100 nav-pill'>
              <OutlineButton />
              <PreviousButton />
              <NextButton />
              <BookMarkButton />
              <AskNovoAI displayMode="icon" containerClassName="ask-novoai" />
            </div>
          </div>
        )}

        {!isHandheld && (
          <React.Fragment>
            <div className='toggle-button-container pl-5'>
              {/* TODO: Open/closed tooltip */}
              <OutlineButton />
            </div>
            <div
              className='text-black h-100 w-100'
              data-qa={config.pendo.lecturePage.navigationArrows}
            >
              <div className='nav-pill'>
                <PreviousButton />
                <div className='divider' />
                <NextButton />
              </div>
            </div>
            <div className="d-flex align-items-center pr-5">
              <BookMarkButton />
              <AskNovoAI displayMode="icon" containerClassName="ml-4" />
            </div>
          </React.Fragment>
        )}
      </div>
      <CourseCompletionStatusModal />
    </nav>
  );
};

const NavigationButton = (props: {
  direction: 'previous' | 'next',
  navigate: () => void,
  color?: string,
  isHighlighted?: boolean
}) => {
  const isNotDesktop = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });
  const clickableContainerStyles = css`
  &:focus{
    outline: 2px solid ${warning} !important;
    outline-offset:0px !important;
  }`;
  const styles = css`
    ${isHandheld && css`
      width: ${doubleSpacing}px;
    `}
    height: ${doubleSpacing}px;
    display: flex;
    flex-direction: ${props.direction === 'previous' ? 'row' : 'row-reverse'};
    background-color: ${props.direction === 'previous' ? white : props.color};
    color: ${props.direction === 'next' && props.color === primary ? white : black};
    justify-content: center;
    align-items: center;
    user-select: none;
    font-weight: 700;
    ${!isHandheld && css`
      padding: ${halfSpacing}px ${standardSpacing}px;
    `}
    gap: ${halfSpacing}px;
    border-radius: 100px;
    :not(.disabled) {
      cursor: pointer;
    }

    &:hover:not(.disabled) {
      opacity: 1.0;
      background: ${props.isHighlighted ? highTide : info};
      color: ${props.isHighlighted ? white : black};
    }

    &.disabled {
      color: ${gray4};
      cursor: not-allowed;
      background: transparent;
    }
 `;

  return (
    <ClickableContainer
      onClick={() => props.navigate?.()}
      disabled={!props.navigate}
      css={clickableContainerStyles}
      aria-label={props.direction === 'previous' ? t.SHARED.PREVIOUS() : t.SHARED.NEXT()}
    >
      <div css={styles} className={`icon-container ${!props.navigate ? 'disabled' : ''}`}>
        <NvIcon size='small' icon={props.direction === 'previous' ? 'arrow-left' : 'arrow-right'} />
        {!isHandheld && (
          <div className='course-title-xs'>
            {(props.direction === 'previous' ? t.SHARED.PREVIOUS() : t.SHARED.NEXT())}
          </div>
        )}
      </div>
    </ClickableContainer>
  );
};


export default LecturePageBottomPanel;

